import React, {useState} from 'react';

import {THEME} from '../../../../common/css/color.styles';
import {trimTrailingZeros} from '../Browse3/charts/duration';
import {
  anchorAboveRect,
  ChartTooltip,
  type TooltipAnchor,
} from '../Browse3/pages/AgentsPage/components/ChartTooltip';

// Value formatting, axis styling, and hover tooltips shared by the indexed bar
// chart and the histogram.

/**
 * Compact numeric label. Sub-1e-4 magnitudes use exponential notation because
 * toFixed(4) would round them to "0".
 */
export const formatNumber = (v: number): string => {
  if (Number.isInteger(v)) {
    return v.toString();
  }
  const abs = Math.abs(v);
  if (abs < 1e-4) {
    return v.toExponential(2);
  }
  return trimTrailingZeros(v.toFixed(4));
};

// `preserveStartEnd` + `minTickGap` let recharts drop colliding labels as the
// container narrows while always keeping the first and last, so a small cell
// shows a readable few and the popover fills in more.
export const SHARED_AXIS_PROPS = {
  tick: {fontSize: 9, fill: THEME.textTertiary},
  tickLine: false,
  axisLine: {stroke: THEME.border},
  interval: 'preserveStartEnd',
  minTickGap: 15,
} as const;

type HoverState = {anchor: TooltipAnchor; label: string};

// The subset of props recharts passes to a custom Bar `shape`: rectangle
// geometry plus the bound datum as `payload`. Recharts types `shape` loosely
// (its callback param is `unknown`), so we narrow to this at the boundary.
type BarShapeProps<T> = {
  x: number;
  y: number;
  width: number;
  height: number;
  payload: T;
};

/**
 * Hover tooltips for an in-cell recharts bar chart. A grid cell's
 * `overflow: hidden` clips recharts' own `<Tooltip>`, so bars render via a
 * custom `shape` that reports the hovered rect and the label floats out through
 * the body-portaled `ChartTooltip`
 *
 * `labelOf` maps a bar's datum to its tooltip text; `fill` is the bar color.
 * Returns the `shape` for `<Bar>` and the tooltip overlay to render.
 */
export const useHoverableBars = <T,>({
  labelOf,
  fill,
}: {
  labelOf: (datum: T) => string;
  fill: string;
}) => {
  const [hover, setHover] = useState<HoverState | null>(null);

  // Param is `unknown` to stay assignable to recharts' `shape` prop; narrow to
  // the fields we read.
  const barShape = (barProps: unknown) => {
    const {x, y, width, height, payload} = barProps as BarShapeProps<T>;
    return (
      <rect
        x={x}
        y={y}
        width={width}
        height={height}
        rx={2}
        fill={fill}
        onMouseEnter={(e: React.MouseEvent<SVGRectElement>) =>
          setHover({
            anchor: anchorAboveRect(e.currentTarget.getBoundingClientRect()),
            label: labelOf(payload),
          })
        }
        onMouseLeave={() => setHover(null)}
      />
    );
  };

  const overlay = (
    <ChartTooltip anchor={hover?.anchor ?? null}>{hover?.label}</ChartTooltip>
  );

  return {barShape, overlay};
};
